/**
 * French (fr) — locale
 * Direction: ltr
 * Font: system sans (same as English)
 * To activate: add 'fr' to SupportedLocale union in i18n/index.tsx
 *              and add fr to LOCALES map
 */
import type { Locale } from './en';

const fr: Locale = {
  meta: {
    appName:  'Maternally',
    tagline:  'impacter des vies, une mère à la fois',
    language: 'Français',
    dir:      'ltr',
    locale:   'fr-FR',
  },

  common: {
    install: { iosHint: "Ajoutez Maternally à votre écran d’accueil : appuyez sur Partager, puis « Sur l’écran d’accueil »." },
    continue:    'Continuer',
    back:        'Retour',
    save:        'Enregistrer',
    cancel:      'Annuler',
    close:       'Fermer',
    loading:     'Chargement…',
    error:       'Une erreur est survenue. Veuillez réessayer.',
    required:    'Requis',
    optional:    'facultatif',
    seeAll:      'Tout voir',
    viewAll:     'Tout voir',
    submit:      'Soumettre',
    send:        'Envoyer',
    next:        'Suivant',
    skip:        'Ignorer pour l\'instant',
    done:        'Terminé',
    confirm:     'Confirmer',
    delete:      'Supprimer',
    edit:        'Modifier',
    search:      'Rechercher',
    noResults:   'Aucun résultat',
    minutes:     '{{n}} min',
    today:       'Aujourd\'hui',
    yesterday:   'Hier',
  },

  nav: {
    home:      'Accueil',
    mood:      'Humeur',
    companion: 'Compagnon',
    learn:     'Apprendre',
    profile:   'Profil',
  },

  verifyEmail: {
    working:       'Confirmation de votre e-mail…',
    oneMoment:     'Un instant.',
    doneTitle:     'Votre e-mail est confirmé',
    doneBody:      'Vous pouvez vous connecter.',
    signIn:        'Se connecter',
    failedTitle:   'Impossible de confirmer ce lien',
    missingToken:  'Il manque le code de vérification dans ce lien.',
    failed:        'Ce lien est invalide ou a expiré.',
    failedHelp:    'Les liens de vérification expirent. Connectez-vous pour en recevoir un nouveau.',
    backToSignIn:  'Retour à la connexion',
  },
  severity: {
    none: 'aucun', minimal: 'minimal', mild: 'léger', moderate: 'modéré',
    moderately_severe: 'modérément sévère', severe: 'sévère', crisis: 'crise',
  },
  auth: {
    restoreAccount: {
      prompt: "Ce compte est programmé pour suppression. Reconnectez-vous pour le restaurer.",
      action: "Restaurer mon compte",
      working: "Restauration…",
      failed: "Nous n’avons pas pu restaurer ce compte.",
    },
    resendVerification: "Envoyez-moi un nouveau lien de vérification",
    sending: "Envoi…",
    verificationResent: "Si ce compte doit être vérifié, un nouveau lien vient d'être envoyé.",
    signIn:             'Se connecter',
    signOut:            'Se déconnecter',
    register:           'Créer un compte',
    email:              'Adresse e-mail',
    password:           'Mot de passe',
    confirmPassword:    'Confirmer le mot de passe',
    forgotPassword:     'Mot de passe oublié ?',
    resetPassword:      'Réinitialiser le mot de passe',
    welcomeBack:        'Bienvenue',
    signInSubtitle:     'Connectez-vous pour continuer votre parcours',
    newToApp:           'Nouvelle sur Maternally ?',
    alreadyHaveAccount: 'Vous avez déjà un compte ?',
    createAccount:      'Créer un compte',
    checkEmail:         'Vérifiez votre e-mail',
    checkEmailBody:     'Nous avons envoyé un lien de vérification à {{email}}. Ouvrez-le pour activer votre compte.',
    backToSignIn:       'Retour à la connexion',
    passwordRules: {
      length:    'Au moins 10 caractères',
      uppercase: 'Une lettre majuscule',
      lowercase: 'Une lettre minuscule',
      number:    'Un chiffre',
      special:   'Un caractère spécial',
    },
    privacyNote: 'En vous inscrivant, vous acceptez nos Conditions d\'utilisation et notre Politique de confidentialité. Vos données de santé sont chiffrées et protégées.',
    immediateSupport: 'Besoin d\'aide immédiate ? →',
  },

  onboarding: {
    dontAskAgain: 'Ne plus me demander',
    welcome:      'Bienvenue sur Maternally',
    whereAreYou:  'Où en êtes-vous dans votre parcours ?',
    stages: {
      trying:     { label: 'Essayer de concevoir',  sub: 'En route vers la grossesse' },
      pregnant:   { label: 'Enceinte',              sub: 'Actuellement en attente' },
      postpartum: { label: 'Post-partum',           sub: 'Après la naissance (jusqu\'à 2 ans)' },
      loss:       { label: 'Perte de grossesse',    sub: 'Fausse couche, mort fœtale ou perte d\'un nourrisson' },
    },
    howFarAlong:   'À combien de semaines en êtes-vous ?',
    whenDidYouGive:'Quand avez-vous accouché ?',
    takYourTime:   'Prenez votre temps',
    lossMessage:   'Nous sommes vraiment désolés pour votre perte. Partagez uniquement ce avec quoi vous vous sentez à l\'aise.',
    gestWeeks:     'Âge gestationnel (semaines)',
    approximate:   'Une valeur approximative est suffisante',
    birthDate:     'Date d\'accouchement (approximative)',
    whatCallYou:   'Comment souhaitez-vous être appelée ?',
    nameOptional:  'Entièrement facultatif — nous pouvons utiliser votre e-mail à la place.',
    firstName:     'Prénom',
    nameEncrypted: '🔒 Votre prénom est chiffré et visible uniquement par vous et votre équipe soignante.',
    fewThings:     'Quelques points importants',
    consent: {
      clinical: { label: 'Partage des données cliniques',    desc: 'Je comprends que mes résultats d\'évaluation et mes données d\'humeur seront partagés avec mon équipe soignante désignée.' },
      push:     { label: 'Rappels bien-être',                desc: 'M\'envoyer des rappels pour compléter mes bilans quotidiens. (Désactivable à tout moment.)' },
      research: { label: 'Contribution à la recherche',      desc: 'Autoriser l\'utilisation de données anonymisées pour améliorer la santé mentale périnatale. Jamais de données personnelles.' },
    },
    letsStart:   'Je suis prête — commençons 🌱',
    privacyNote: 'En continuant, vous acceptez nos Conditions d\'utilisation et notre Politique de confidentialité.',
  },

  dashboard: {
    greeting: {
      morning:   'Bonjour',
      afternoon: 'Bon après-midi',
      evening:   'Bonsoir',
    },
    stages: {
      trying:     'Essayer de concevoir',
      pregnant:   'Enceinte',
      postpartum: 'Post-partum',
      loss:       'Perte de grossesse',
    },
    riskLevels: {
      low:      'risque faible',
      moderate: 'risque modéré',
      high:     'risque élevé',
      crisis:   '⚠ Soutien de crise actif',
    },
    crisis: {
      supportTitle: "Les choses semblent difficiles en ce moment. Le soutien est là quand vous le voulez.",
      title:   'Vous n\'êtes pas seule. Un soutien immédiat est disponible.',
      more:    'Plus de ressources',
    },
    quickActions: {
      logMood:   'Humeur',
      talkItOut: 'En parler',
      checkIn:   'Bilan',
      resources: 'Ressources',
    },
    yourWeek:       'Votre semaine',
    recentCheckIns: 'Bilans récents',
    carePlan:       'Votre plan de soins',
    carePlanSub:    'Consultez vos objectifs et les tâches bien-être du jour',
    chartLegend: {
      mood:    'Humeur',
      anxiety: 'Anxiété',
    },
  },

  mood: {
    title:        'Comment vous sentez-vous aujourd\'hui ?',
    subtitle:     'Cela prend environ 60 secondes et nous aide à mieux vous soutenir.',
    overallMood:  'Humeur générale',
    scale: {
      veryLow: 'Très bas',
      low:     'Bas',
      okay:    'Correct',
      good:    'Bien',
      great:   'Excellent',
    },
    energy:       'Niveau d\'énergie',
    anxiety:      'Niveau d\'anxiété',
    sleep:        'Sommeil la nuit dernière',
    energyLow:    'Épuisée',
    energyHigh:   'Énergique',
    anxietyLow:   'Calme',
    anxietyHigh:  'Très anxieuse',
    sleepLow:     '0 h',
    sleepHigh:    '12 h',
    tags:         'Comment le décririez-vous ?',
    note:         'Autre chose en tête ?',
    notePlaceholder: 'Écrivez librement — c\'est privé et chiffré…',
    save:         'Enregistrer le bilan du jour',
    saved:        'Enregistré !',
    savedSub:     'Votre bilan d\'aujourd\'hui a été sauvegardé.',
    logAnother:   'Saisir une autre entrée',
    moodTags: [
      'Anxieuse', 'Fatiguée', 'Dépassée', 'Optimiste',
      'Reconnaissante', 'Pleurante', 'Déconnectée', 'Bonne liaison',
      'Seule', 'Soutenue', 'Brouillard mental', 'Présente',
    ],
  },

  assessment: {
    title: 'Bilan',
    instruments: {
      EPDS: 'Échelle de dépression postnatale d\'Édimbourg',
      GAD7: 'Dépistage de l\'anxiété (GAD-7)',
      PHQ9: 'Dépistage de la dépression (PHQ-9)',
    },
    intro: {
      description: 'Il s\'agit d\'un outil de dépistage validé utilisé par les cliniciens pour comprendre comment vous vous êtes sentie au cours des 7 derniers jours. Il n\'y a pas de bonnes ou mauvaises réponses.',
      questions:   '{{n}} questions',
      time:        'Environ 5 minutes',
      shared:      'Les résultats sont partagés avec votre équipe soignante',
      begin:       'Commencer le bilan',
      notNow:      'Pas maintenant',
    },
    inPast7Days: 'Au cours des 7 derniers jours…',
    crisisNotice:'Votre réponse à cette question nous aide à vous assurer le bon soutien.',
    submitting:  'Analyse de vos réponses…',
    result: {
      score:           'Score',
      whatHappensNext: 'Prochaines étapes',
      backHome:        'Retour à l\'accueil',
      talkCompanion:   'Parler à votre compagnon',
      crisis: {
        title:       'Vous n\'êtes pas seule — de l\'aide est disponible maintenant',
        teamAlerted: 'Votre équipe soignante a été alertée et vous contactera prochainement.',
      },
    },
  },

  companion: {
    welcome: "Bonjour, je suis là avec vous. C'est un espace sûr pour partager ce que vous ressentez — quoi que ce soit qui vous préoccupe. Comment allez-vous aujourd'hui ?",
    name:        'Votre compagnon',
    subtitle:    'Confidentiel · Cliniquement informé · Toujours disponible',
    needHelp:    'Besoin d\'aide maintenant ?',
    placeholder: 'Partagez ce qui vous préoccupe…',
    disclaimer:  'Ceci ne remplace pas une prise en charge professionnelle.',
    immediateHelp: 'Besoin d\'aide immédiate ?',
    starters: [
      'Je me sens vraiment dépassée ces derniers temps',
      'J\'ai du mal à dormir et ça affecte mon humeur',
      'Je n\'arrive pas à créer le lien avec mon bébé comme je l\'espérais',
      'Je suis anxieuse à l\'idée de devenir mère',
      'Je ne sais pas si ce que je ressens est normal',
    ],
    crisis: {
      title:   'Soutien immédiat',
      message: 'Vous n\'êtes pas seule. Ces ressources sont gratuites et disponibles maintenant.',
    },
  },

  content: {
    title:    'Apprendre',
    subtitle: 'Ressources fondées sur des preuves pour votre parcours',
    search:   'Rechercher des articles, exercices…',
    filters: {
      all:      'Tout',
      article:  'Article',
      exercise: 'Exercice',
      audio:    'Audio',
      module:   'Module',
    },
    categories: ['Anxiété', 'Sommeil', 'Lien', 'Soin de soi', 'Relations', 'Deuil'],
    empty:     'Aucun contenu trouvé. Essayez une autre recherche.',
    minRead:   '{{n}} min de lecture',
    minDuration:'{{n}} min',
  },

  care: {
    title:          'Plan de soins',
    noplan:         'Pas encore de plan de soins',
    noplanSub:      'Complétez votre première évaluation et un responsable des soins créera un plan personnalisé pour vous dans les 48 heures.',
    startAssessment:'Commencer votre premier bilan',
    goals:          'Vos objectifs',
    todaysTasks:    'Tâches du jour',
    complete:       'Marquer comme terminé',
    completed:      'Terminé',
    nextReview:     'Prochaine révision avec votre équipe soignante :',
    categories: {
      self_care:  'Soin de soi',
      therapy:    'Thérapie',
      medication: 'Médicament',
      social:     'Social',
      exercise:   'Exercice',
      sleep:      'Sommeil',
      nutrition:  'Nutrition',
      other:      'Autre',
    },
  },

  profile: {
    deleteAccount: {
      title: "Supprimer le compte",
      sub: "Supprimer votre compte et tout ce qu’il contient",
      confirmTitle: "Supprimer votre compte ?",
      confirmBody: "Ce téléphone sera vidé immédiatement : quiconque le prend en main n’y trouvera rien. Vos données sont conservées 30 jours si vous changez d’avis ; reconnectez-vous avec votre e-mail et votre mot de passe pour les restaurer. Ensuite, elles sont détruites.",
      retainNote: "Nous conservons deux choses et préférons le dire : une trace indiquant qu’un compte a existé et a été supprimé, sans détail personnel, et toute crise transmise à un clinicien.",
      cancel: "Garder mon compte",
      confirm: "Supprimer mon compte",
      working: "Suppression…",
      failed: "Nous n’avons pas pu supprimer votre compte. Réessayez ou écrivez à support@maternally.care.",
    },
    completeSetup: 'Terminer la configuration',
    completeSetupSub: 'Ajoutez votre prénom et votre étape quand vous le souhaitez',
    notSetYet: 'Pas encore renseigné',
    title:             'Profil',
    myJourney:         'Mon parcours',
    stage:             'Étape',
    epdsScreening:     'Bilan EPDS',
    takeNow:           'Faire maintenant →',
    carePlan:          'Plan de soins',
    view:              'Voir →',
    notifications:     'Notifications',
    dailyReminders:    'Rappels d\'humeur quotidiens',
    dailyRemindersSub: 'Un rappel doux chaque matin',
    smsAlerts:         'Alertes SMS',
    smsAlertsSub:      'Messages de l\'équipe soignante par SMS',
    privacy:           'Confidentialité et données',
    research:          'Contribuer à la recherche',
    researchSub:       'Données anonymisées uniquement — aide à améliorer les soins pour toutes les mères',
    encryption:        'Chiffrement des données',
    compliance:        'Conformité',
    support:           'Support',
    crisisResources:   'Ressources de crise',
    appVersion:        'Version de l\'application',
    signOut:           'Se déconnecter',
    stageName: {
      trying:     'Essayer de concevoir',
      pregnant:   'Enceinte',
      postpartum: 'Post-partum',
      loss:       'Perte de grossesse',
    },
  },

  crisis: {
    title:     'Vous n\'êtes pas seule',
    subtitle:  'De l\'aide est disponible maintenant — gratuite, confidentielle, à portée d\'appel.',
    fact:      'Les troubles de l\'humeur et de l\'anxiété périnatals sont la complication la plus fréquente de la grossesse et du post-partum — touchant 1 mère sur 5. Avec le bon soutien, la guérison est possible et très courante.',
    factTitle: 'Vous comptez — et il en va de même pour obtenir du soutien',
    backToApp: '← Retour à Maternally',
  },

  languageSwitcher: {
    label:   'Langue',
    english: 'English',
    arabic:  'العربية',
    french:  'Français',
  },
};

export default fr;
